import type { ReactNode } from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { TabItem } from '@/components/TabBar'
import styles from './TabLabel.module.css'

export interface TabLabelProps {
  children?: ReactNode
  /**
   * The first validation error of the tab's fields. Pass it to the tab's
   * `tooltip` too, which is what describes the error to screen readers.
   */
  error?: string
}

/**
 * A tab's label, marked when the tab has validation errors.
 */
export function TabLabel({ children, error }: TabLabelProps) {
  return (
    <span className={cx(styles.root, error && styles.error)}>
      {children}
      {error && (
        <span className={styles.marker}>
          <Icon name="status-alert" size={12} aria-hidden="true" />
        </span>
      )}
    </span>
  )
}

export interface ErrorTabItemProps {
  title: string
  /**
   * The first validation error of the tab's fields.
   */
  error?: string
  isActive: boolean
  badgeCount?: number
  onPress: () => unknown
  testId?: string
}

/**
 * An inspector tab, marked when its fields have validation errors and
 * explaining the first one on hover and keyboard focus.
 */
export function ErrorTabItem({
  title,
  error,
  isActive,
  badgeCount,
  onPress,
  testId,
}: ErrorTabItemProps) {
  return (
    <TabItem
      active={isActive}
      badgeCount={badgeCount}
      badgePosition="right"
      tooltip={error}
      onPress={onPress}
      testId={testId}
    >
      <TabLabel error={error}>{title}</TabLabel>
    </TabItem>
  )
}
